import { CodeHighlightNode, CodeNode } from "@lexical/code";
import {
  $isLinkNode,
  AutoLinkNode,
  LinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  Transformer,
  TRANSFORMERS,
} from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingNode,
  HeadingTagType,
  QuoteNode,
} from "@lexical/rich-text";
import { $isAtNodeEnd, $setBlocksType } from "@lexical/selection";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { $getNearestNodeOfType } from "@lexical/utils";
import {
  FontBoldIcon,
  FontItalicIcon,
  Link1Icon,
  PlusIcon,
  UnderlineIcon,
} from "@radix-ui/react-icons";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  EditorThemeClasses,
  ElementNode,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  PASTE_COMMAND,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from "lexical";
import { ChevronDownIcon } from "lucide-react";
import { use, useCallback, useEffect, useState } from "react";

import { ReadonlyEditorContext } from "@/components/canvas-editor/utils";
import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const myMarkdownTransformers: Transformer[] = [
  ...TRANSFORMERS,
  {
    format: ["underline"],
    tag: "~",
    type: "text-format",
    intraword: true,
  },
];

const theme: EditorThemeClasses = {
  text: {
    underline: "underline",
  },
  link: "text-blue-dim underline",
};

const SUPPORTED_URL_PROTOCOLS = new Set([
  "http:",
  "https:",
  "mailto:",
  "sms:",
  "tel:",
]);

function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);

    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return "about:blank";
    }
  } catch {
    return url;
  }
  return url;
}

function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();

  if (anchorNode === focusNode) {
    return anchorNode;
  }

  const isBackward = selection.isBackward();

  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}

const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    $setBlocksType(selection, () => $createParagraphNode());
  });
};

const formatHeading = (
  editor: LexicalEditor,
  blockType: string,
  headingSize: HeadingTagType,
) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};

const formatBulletList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "bullet") {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

const formatNumberedList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "number") {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

const formatQuote = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "quote") {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  }
};

const blockTypeToBlockName = {
  bullet: "Bulleted List",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
};

function BlockFormatButton({
  editor,
  blockType,
}: {
  editor: LexicalEditor;
  blockType: keyof typeof blockTypeToBlockName;
}) {
  return (
    <DropdownMenu>
      <MenuTrigger asChild>
        <Button variant="ghost" size="sm">
          {blockTypeToBlockName[blockType]}
          <ChevronDownIcon />
        </Button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuItem onClick={() => formatParagraph(editor)}>Normal</MenuItem>
        <MenuItem onClick={() => formatHeading(editor, blockType, "h1")}>
          Heading 1
        </MenuItem>
        <MenuItem onClick={() => formatHeading(editor, blockType, "h2")}>
          Heading 2
        </MenuItem>
        <MenuItem onClick={() => formatHeading(editor, blockType, "h3")}>
          Heading 3
        </MenuItem>
        <MenuItem onClick={() => formatBulletList(editor, blockType)}>
          Bullet List
        </MenuItem>
        <MenuItem onClick={() => formatNumberedList(editor, blockType)}>
          Numbered List
        </MenuItem>
        <MenuItem onClick={() => formatQuote(editor, blockType)}>
          Quote
        </MenuItem>
      </MenuContent>
    </DropdownMenu>
  );
}

interface ToolbarState {
  isLink: boolean;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  blockType: keyof typeof blockTypeToBlockName;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<ToolbarState | null>(null);

  const $updateState = useCallback(() => {
    const selection = $getSelection();

    if (!selection) {
      return null;
    }

    const state: ToolbarState = {
      isLink: false,
      isBold: false,
      isItalic: false,
      isUnderline: false,
      blockType: "paragraph" as const,
    };

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      state.isLink = $isLinkNode(parent) || $isLinkNode(node);
      state.isBold = selection.hasFormat("bold");
      state.isItalic = selection.hasFormat("italic");
      state.isUnderline = selection.hasFormat("underline");

      let blockType = $isHeadingNode(node) ? node.getTag() : node.getType();

      if ($isListNode(node)) {
        const anchorNode = selection.anchor.getNode();
        const parentList = $getNearestNodeOfType<ListNode>(
          anchorNode,
          ListNode,
        );

        blockType = parentList ? parentList.getListType() : node.getListType();
      }

      if (blockType in blockTypeToBlockName) {
        state.blockType = blockType as keyof typeof blockTypeToBlockName;
      }
    }

    setState(state);
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        $updateState();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateState]);

  return (
    <div className="flex items-center gap-0.5 p-1">
      <BlockFormatButton
        editor={editor}
        blockType={state?.blockType || "paragraph"}
      />
      <Separator orientation="vertical" className="h-5 mx-0.5" />
      <Button
        size="smIcon"
        variant={state?.isBold ? "action" : "ghost"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
      >
        <FontBoldIcon />
      </Button>
      <Button
        size="smIcon"
        variant={state?.isItalic ? "action" : "ghost"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
      >
        <FontItalicIcon />
      </Button>
      <Button
        size="smIcon"
        variant={state?.isUnderline ? "action" : "ghost"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
        }}
      >
        <UnderlineIcon />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="smIcon" variant={state?.isLink ? "action" : "ghost"}>
            <span className="sr-only">Insert link</span>
            <Link1Icon />
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const formData = new FormData(e.currentTarget);
              const link = formData.get("link") as string;

              if (link) {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(link));
              } else {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              }
            }}
          >
            <Label htmlFor="link">Link</Label>
            <div className="flex items-center gap-2">
              <Input id="link" name="link" />
              <Button size="icon" type="submit">
                <span className="sr-only">Apply link</span>
                <PlusIcon />
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: unknown) {
  console.error(error);
}

/**
 * Whether some pasted text is tldraw's own clipboard payload.
 *
 * Copying shapes on the canvas puts `{"type":"application/tldraw","kind":
 * "content",...}` on the clipboard as text/plain, so a paste landing in a text
 * field inserts the whole serialized selection as prose.
 */
function isCanvasClipboardPayload(text: string) {
  const head = text.trimStart().slice(0, 200);
  return head.startsWith("{") && head.includes('"application/tldraw"');
}

/**
 * Refuse a paste of copied canvas nodes.
 *
 * This is not a hypothetical: component 1800 was saved with 4,108 characters of
 * that payload as its description, and 1203 still carries the same blob
 * appended to a real sentence. Both authors had copied nodes on the canvas and
 * pasted while focus was still in a description field -- and nothing about the
 * result says "you pasted in the wrong place", it just looks like the field
 * ate a wall of JSON.
 *
 * Swallowing it silently would be its own small mystery, so the toast says
 * where the nodes were meant to go. The paste is refused rather than cleaned up
 * because there is no sensible text to insert: the payload is a serialized
 * selection, not a description of one.
 */
function CanvasClipboardGuardPlugin() {
  const [editor] = useLexicalComposerContext();
  const { toast } = useToast();

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          // PASTE_COMMAND also fires for InputEvent/KeyboardEvent, neither of
          // which carries clipboard data -- those are left to the rich-text
          // plugin by returning false.
          if (!(event instanceof ClipboardEvent)) return false;

          const text = event.clipboardData?.getData("text/plain");
          if (!text || !isCanvasClipboardPayload(text)) return false;

          event.preventDefault();
          toast({
            title: "That's a copied canvas node, not text",
            description:
              "Click the canvas first, then paste. Pasting it here would store the node's raw JSON as your description.",
          });
          return true;
        },
        // Must outrank the rich-text plugin's own paste handler, which would
        // otherwise insert the text before this ever ran.
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, toast],
  );

  return null;
}

export function RichTextEditor({
  value,
  onChange,
  className,
  variant = "inline",
  placeholder = "Enter some text...",
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  variant?: "full" | "inline";
  placeholder?: string;
  id?: string;
}) {
  const isReadOnly = use(ReadonlyEditorContext);
  const initialConfig: InitialConfigType = {
    namespace: "MyEditor",
    editable: !isReadOnly,
    theme,
    onError,
    nodes: [
      HorizontalRuleNode,
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
    ],
    editorState: () =>
      $convertFromMarkdownString(value, myMarkdownTransformers),
  };

  return (
    <div
      className={cn(
        "relative rounded-md",
        variant === "full" && "border border-slate-8 dark:border-slatedark-8",
        variant === "inline" && "w-full",
      )}
    >
      <LexicalComposer initialConfig={initialConfig}>
        {variant === "full" && (
          <>
            <ToolbarPlugin />
            <LinkPlugin />
          </>
        )}
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              id={id}
              className={cn(
                "notes-content",
                variant === "full" &&
                  "min-h-[80px] w-full rounded-b-md border-t border-slate-8 dark:border-slatedark-8 bg-slate-subtle px-3 py-2 text-base ring-offset-background placeholder:text-slate-dim focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                className,
              )}
            />
          }
          placeholder={
            <div
              className={cn(
                "text-slate-dim absolute pointer-events-none text-lg",
                variant === "full" ? "top-13 left-3" : "top-1.5",
              )}
            >
              {placeholder}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <CanvasClipboardGuardPlugin />
        <HistoryPlugin />
        <AutoFocusPlugin />
        <MarkdownShortcutPlugin transformers={myMarkdownTransformers} />
        <ListPlugin />
        <OnChangePlugin
          onChange={(e) => {
            e.read(() => {
              const markdown = $convertToMarkdownString(myMarkdownTransformers);
              // remove zero width spaces
              const cleanedMarkdown = markdown
                .replace(/\u200B/g, "")
                .replace(/\\\\/g, "\\");

              onChange(cleanedMarkdown);
            });
          }}
        />
      </LexicalComposer>
    </div>
  );
}
